'use client';

import MuiButton, {type ButtonProps as MuiButtonProps} from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import {styled, type CSSObject, type SxProps, type Theme} from '@mui/material/styles';
import {forwardRef, type ReactNode} from 'react';
import {Typography, type TypographyProps} from './Typography';

export type OlympusButtonVariant = 'contained-dark' | 'outlined-dark' | 'contained-light' | 'outlined-light' | 'contained-overlay' | 'link';

export type OlympusButtonSize = 'lg' | 'sm';

type SxEntry = Exclude<SxProps<Theme>, readonly unknown[]>;

export type ButtonLabelProps = Pick<TypographyProps, 'size' | 'weight' | 'family' | 'mobileSize' | 'mobileWeight'> & {sx?: SxEntry};

export interface ButtonProps extends Omit<MuiButtonProps, 'variant' | 'size' | 'color' | 'startIcon' | 'endIcon'> {
  variant?: OlympusButtonVariant;
  size?: OlympusButtonSize;
  loading?: boolean;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  labelProps?: ButtonLabelProps;
  target?: string;
  rel?: string;
}

interface StyledButtonProps {
  oVariant: OlympusButtonVariant;
  oSize: OlympusButtonSize;
  oLoading: boolean;
}

// Figma [MASTER] Button — Large (node 2582:20071) is 51px tall with a 160px
// floor so CTAs line up; Small (node 2582:20159) is 34px and hugs its label.
// minHeight, not padding, sets the height: the label is trimmed to its cap
// height, so padding alone would drift between bordered and borderless variants.
const SIZE_STYLES = {
  lg: {
    fontSize: 'body-2',
    paddingBlock: 1.75,
    paddingInline: 3.5,
    minHeight: 51,
    minWidth: 160,
    gap: 1,
  },
  sm: {
    fontSize: 'caption',
    paddingBlock: 1.25,
    paddingInline: 1.5,
    minHeight: 34,
    minWidth: 'auto',
    gap: 0.5,
  },
} as const;

type ButtonColors = Theme['palette']['colors'];

const VARIANT_STYLES: Record<OlympusButtonVariant, (colors: ButtonColors) => CSSObject> = {
  'contained-dark': colors => ({
    backgroundColor: colors.surface.primary.default,
    color: colors.text.primary.onColor,
    '&:hover': {backgroundColor: colors.surface.primary.hover, boxShadow: 'none'},
    '&:active': {backgroundColor: colors.surface.primary.active, boxShadow: 'none'},
    '&.Mui-disabled': {backgroundColor: colors.surface.neutral.disabled, color: colors.text.neutral.disabled},
  }),
  'outlined-dark': colors => ({
    backgroundColor: 'transparent',
    color: colors.text.primary.default,
    border: `1px solid ${colors.border.primary.default}`,
    '&:hover': {
      backgroundColor: colors.surface.neutral.inverseSubtleHover,
      borderColor: colors.border.primary.hover,
      color: colors.text.primary.hover,
      boxShadow: 'none',
    },
    '&:active': {
      backgroundColor: colors.surface.primary.subtle,
      borderColor: colors.border.primary.hover,
      color: colors.text.primary.active,
      boxShadow: 'none',
    },
    '&.Mui-disabled': {
      backgroundColor: colors.surface.neutral.disabled,
      color: colors.text.neutral.disabled,
      borderColor: colors.border.neutral.disabled,
    },
  }),
  'contained-light': colors => ({
    backgroundColor: colors.surface.neutral.default,
    color: colors.text.primary.default,
    border: `1px solid ${colors.border.primary.default}`,
    '&:hover': {backgroundColor: colors.surface.neutral.hover, boxShadow: 'none'},
    '&:active': {
      backgroundColor: colors.surface.neutral.active,
      borderColor: colors.border.primary.hover,
      color: colors.text.primary.active,
      boxShadow: 'none',
    },
    '&.Mui-disabled': {
      backgroundColor: colors.surface.neutral.disabled,
      borderColor: colors.surface.neutral.disabled,
      color: colors.text.neutral.disabled,
    },
  }),
  'outlined-light': colors => ({
    backgroundColor: 'transparent',
    color: colors.text.neutral.inverse,
    border: `1px solid ${colors.border.neutral.inverseStrong}`,
    '& svg': {color: colors.icon.neutral.inverse},
    '&:hover': {backgroundColor: colors.surface.neutral.inverseSubtleHover, boxShadow: 'none'},
    '&:active': {backgroundColor: colors.surface.lightOverlay, boxShadow: 'none'},
    '&.Mui-disabled': {
      color: colors.text.neutral.disabled,
      borderColor: colors.border.neutral.disabled,
      '& svg': {color: colors.icon.neutral.disabled},
    },
  }),
  'contained-overlay': colors => ({
    backgroundColor: colors.surface.darkOverlayStrong,
    color: colors.text.neutral.inverse,
    border: `1px solid ${colors.border.neutral.inverse}`,
    '& svg': {color: colors.icon.neutral.inverse},
    '&:hover': {backgroundColor: colors.surface.darkOverlayStrongHover, boxShadow: 'none'},
    '&:active': {backgroundColor: colors.surface.neutral.inverse, boxShadow: 'none'},
    '&.Mui-disabled': {
      backgroundColor: colors.surface.darkOverlay,
      color: colors.text.neutral.disabled,
      '& svg': {color: colors.icon.neutral.disabled},
    },
  }),
  link: colors => ({
    backgroundColor: 'transparent',
    color: colors.text.primary.default,
    textDecoration: 'none',
    '& svg': {color: colors.icon.primary.default},
    '&:hover': {color: colors.text.primary.hover, textDecoration: 'underline', '& svg': {color: colors.icon.primary.hover}},
    '&:active': {color: colors.text.primary.active, textDecoration: 'underline', '& svg': {color: colors.icon.primary.hover}},
    '&.Mui-disabled': {color: colors.text.neutral.disabled, textDecoration: 'none', '& svg': {color: colors.icon.neutral.disabled}},
  }),
};

// Figma only defines a Loading state for Contained / Dark.
const LOADING_STYLES: Partial<Record<OlympusButtonVariant, (colors: ButtonColors) => CSSObject>> = {
  'contained-dark': colors => ({
    '&.Mui-disabled': {
      backgroundColor: colors.surface.primary.disabled,
      color: colors.text.neutral.disabled,
      '& .MuiCircularProgress-root': {color: colors.icon.primary.default},
    },
  }),
};

const StyledButton = styled(MuiButton, {
  shouldForwardProp: propName => !['oVariant', 'oSize', 'oLoading'].includes(propName as string),
})<StyledButtonProps>(({theme, oVariant, oSize, oLoading}) => {
  const colors = theme.palette.colors;
  const sizeStyles = SIZE_STYLES[oSize];
  const chrome =
    oVariant === 'link'
      ? {minHeight: 'auto', padding: 0, borderRadius: theme.radii.full}
      : {
          paddingBlock: theme.spacing(sizeStyles.paddingBlock),
          paddingInline: theme.spacing(sizeStyles.paddingInline),
          minHeight: sizeStyles.minHeight,
          minWidth: sizeStyles.minWidth,
          borderRadius: theme.radii.button,
        };
  return {
    boxShadow: 'none',
    whiteSpace: 'nowrap',
    // display/align/justify are inherited from MUI ButtonBase (inline-flex, centered).
    gap: theme.spacing(sizeStyles.gap),
    '&:focus-visible': {outline: `2px solid ${colors.border.focus.default}`, outlineOffset: 2},
    ...chrome,
    ...VARIANT_STYLES[oVariant](colors),
    ...(oLoading ? LOADING_STYLES[oVariant]?.(colors) : undefined),
  };
});

function labelSx(variant: OlympusButtonVariant, override: SxEntry | undefined): SxProps<Theme> {
  const base = {
    color: 'inherit',
    letterSpacing: 0,
    textTransform: 'capitalize',
    ...(variant !== 'link' && {textBox: 'trim-both cap alphabetic'}),
  } as const;
  return [base, override ?? false];
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({variant = 'contained-dark', size = 'lg', loading = false, iconLeft, iconRight, labelProps, disabled, children, ...props}, ref) => {
    const isDisabled = disabled || loading;
    return (
      <StyledButton
        ref={ref}
        oVariant={variant}
        oSize={size}
        oLoading={loading}
        disabled={isDisabled}
        disableRipple={variant === 'link'}
        {...props}
      >
        {loading && <CircularProgress size={18} color="inherit" />}
        {!loading && iconLeft}
        {children != null && children !== false && (
          <Typography
            as="span"
            family="primary"
            size={SIZE_STYLES[size].fontSize}
            weight="medium"
            {...labelProps}
            sx={labelSx(variant, labelProps?.sx)}
          >
            {children}
          </Typography>
        )}
        {!loading && iconRight}
      </StyledButton>
    );
  },
);

Button.displayName = 'Button';
